/**
 * Regras da lista de Cancelamentos que antes moravam dentro do JSX da tela
 * (#6121). Saíram para cá quando as ações passaram da coluna da tabela para o
 * modal da solicitação: mudar de lugar é justamente quando uma condição se perde
 * sem ninguém notar — alguém conclui sem validar, ou não vê que o WhatsApp falhou.
 */
import type { StatusSolicitacaoCancelamento } from './cancelamentos.ts';

export type RotuloResposta = 'responder' | 'reenviar' | 'respondida';

export interface AcoesSolicitacao {
    /** "Validar cancelamento": conferência dos valores antes do distrato. */
    podeValidar: boolean;
    /** "Gerar distrato": só depois de validada, e enquanto não há distrato ativo. */
    podeGerarDistrato: boolean;
    /** Retorno ao cliente: os valores da mensagem vêm do que foi validado. */
    podeResponder: boolean;
    /** Qual versão do botão de retorno mostrar (só vale com `podeResponder`). */
    rotuloResposta: RotuloResposta;
    podeConcluir: boolean;
    podeDesistir: boolean;
}

/** Solicitação ainda em trabalho — as únicas em que alguma ação muda algo. */
export function estaEmAndamento(status: StatusSolicitacaoCancelamento): boolean {
    return status === 'aberta' || status === 'em_andamento';
}

export function acoesDisponiveis(
    s: {
        status: StatusSolicitacaoCancelamento;
        distratoStatus: string;
        validadoEm: string | null;
        respostaEnviadaEm: string | null;
    },
    envio: { precisaReenviar: boolean },
): AcoesSolicitacao {
    const ativa = estaEmAndamento(s.status);
    const semDistratoAtivo = s.distratoStatus !== 'ativo';
    const validada = !!s.validadoEm;
    return {
        podeValidar: ativa && semDistratoAtivo && !validada,
        podeGerarDistrato: ativa && semDistratoAtivo && validada,
        podeResponder: ativa && validada,
        // Falha manda sobre "já respondido": o registro de envio existe, mas o
        // cliente não recebeu — mostrar "Resposta enviada" foi o erro do #5830.
        rotuloResposta: envio.precisaReenviar ? 'reenviar' : s.respostaEnviadaEm ? 'respondida' : 'responder',
        podeConcluir: ativa,
        podeDesistir: ativa,
    };
}

/** Selo de "resposta pendente de reenvio" na lista: só onde o botão existiria. */
export function precisaReenviarNaLista(
    s: { status: StatusSolicitacaoCancelamento; validadoEm: string | null },
    envio: { precisaReenviar: boolean },
): boolean {
    return estaEmAndamento(s.status) && !!s.validadoEm && envio.precisaReenviar;
}

export interface Pagina<T> {
    itens: T[];
    /** Página efetiva (1-based), já presa ao intervalo válido. */
    pagina: number;
    totalPaginas: number;
    /** Posição do primeiro e do último item exibidos, para "1–50 de 214". */
    de: number;
    ate: number;
    total: number;
}

export const TAMANHO_PAGINA_CANCELAMENTOS = 50;

/**
 * Fatia a lista para exibição. NÃO use o resultado para exportar: o Exportar
 * leva o filtro inteiro, não só a página da tela.
 */
export function paginar<T>(lista: readonly T[], pagina: number, tamanho = TAMANHO_PAGINA_CANCELAMENTOS): Pagina<T> {
    const total = lista.length;
    const porPagina = Math.max(1, Math.floor(tamanho) || 1);
    const totalPaginas = Math.max(1, Math.ceil(total / porPagina));
    const atual = Math.min(Math.max(1, Math.floor(pagina) || 1), totalPaginas);
    const inicio = (atual - 1) * porPagina;
    const itens = lista.slice(inicio, inicio + porPagina);
    return {
        itens,
        pagina: atual,
        totalPaginas,
        de: total === 0 ? 0 : inicio + 1,
        ate: inicio + itens.length,
        total,
    };
}

/** Página em que está o item de índice `indice` (0-based) — para pular até ele. */
export function paginaDoIndice(indice: number, tamanho = TAMANHO_PAGINA_CANCELAMENTOS): number {
    if (indice < 0) return 1;
    return Math.floor(indice / Math.max(1, tamanho)) + 1;
}

// ---------------------------------------------------------------------------
// Data da solicitação na tela (#6122)
// ---------------------------------------------------------------------------

const MESES_ABA: Record<string, number> = {
    JANEIRO: 1, FEVEREIRO: 2, MARCO: 3, ABRIL: 4, MAIO: 5, JUNHO: 6,
    JULHO: 7, AGOSTO: 8, SETEMBRO: 9, OUTUBRO: 10, NOVEMBRO: 11, DEZEMBRO: 12,
};

/** Mês e ano da aba da planilha, lidos de "ARQUIVO.xlsx — MAIO 2026 (linha 6)". */
export function mesDaAba(importOrigemPlanilha: string | null | undefined): { mes: number; ano: number } | null {
    const texto = String(importOrigemPlanilha || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
    const m = texto.match(/—\s*([A-Z]+)\s+(\d{4})/);
    if (!m || !MESES_ABA[m[1]]) return null;
    return { mes: MESES_ABA[m[1]], ano: Number(m[2]) };
}

export interface DataExibida {
    /** Data a mostrar (AAAA-MM-DD). */
    iso: string;
    /** Como foi ajustada a partir da aba, ou null se é a data gravada. */
    ajuste: 'dia_mes_trocados' | 'ano_da_planilha' | null;
    /** Data no futuro que a aba não explica — alguém precisa conferir na planilha. */
    conferir: boolean;
    /** A data como está gravada no banco. */
    original: string;
}

/**
 * Data da solicitação como a TELA deve mostrar. Não altera o banco nem a exportação.
 *
 * A planilha histórica foi digitada parte com dia primeiro e parte com mês
 * primeiro (Excel em inglês), e a importação assume dd/mm quando os dois cabem
 * — então "12/05" de maio entrou como 5 de dezembro. Mas cada registro guarda a
 * aba de onde veio ("MAIO 2026"), e ela desempata. Medido em 28/09/2026 nos 163
 * importados: 90 batiam com a aba, 44 tinham dia e mês trocados, 6 tinham o ano
 * errado (26/04/2030 na aba de abril de 2026) e 23 não batiam — quase todos
 * pedidos do fim de um mês lançados na aba seguinte, que ficam como estão.
 *
 * Só ajusta quando a aba prova o erro: trocar dia e mês dá exatamente o mês e o
 * ano da aba, ou o mês bate e só o ano destoa.
 */
export function dataSolicitacaoExibida(
    s: { data_solicitacao: string; origem: string; importOrigemPlanilha: string | null },
    hojeIso: string,
): DataExibida {
    const original = String(s.data_solicitacao || '').slice(0, 10);
    const base: DataExibida = { iso: original, ajuste: null, conferir: false, original };
    const [ano, mes, dia] = original.split('-').map(Number);
    if (!ano || !mes || !dia) return base;

    const aba = s.origem === 'importacao' ? mesDaAba(s.importOrigemPlanilha) : null;
    const pad = (n: number) => String(n).padStart(2, '0');
    let resultado = base;
    if (aba && !(mes === aba.mes && ano === aba.ano)) {
        if (dia === aba.mes && ano === aba.ano) {
            resultado = { ...base, iso: `${aba.ano}-${pad(dia)}-${pad(mes)}`, ajuste: 'dia_mes_trocados' };
        } else if (mes === aba.mes && ano !== aba.ano) {
            resultado = { ...base, iso: `${aba.ano}-${pad(mes)}-${pad(dia)}`, ajuste: 'ano_da_planilha' };
        }
    }
    return { ...resultado, conferir: resultado.iso > hojeIso.slice(0, 10) };
}

// ---------------------------------------------------------------------------
// Retorno do cliente depois da resposta da Vigor (#6127)
// ---------------------------------------------------------------------------

/** Dias corridos sem retorno até o selo ficar vermelho. Decisão de 28/09/2026. */
export const DIAS_ALERTA_SEM_RETORNO_CLIENTE = 7;

export type EstadoRetornoCliente =
    | { estado: 'sem_resposta_vigor' }
    | { estado: 'aguardando'; dias: number; atrasado: boolean }
    | { estado: 'retornou'; tipo: string };

const diaSP = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

/**
 * Onde está a conversa com o cliente. Só avisa — não trava nada: o processo de
 * cancelamento segue mesmo que o cliente nunca responda.
 * Solicitação encerrada (concluída ou desistência) não fica "aguardando".
 */
export function estadoRetornoCliente(
    s: {
        status: StatusSolicitacaoCancelamento;
        respostaEnviadaEm: string | null;
        clienteRetornoEm?: string | null;
        clienteRetornoTipo?: string | null;
    },
    hojeIso: string,
): EstadoRetornoCliente {
    if (s.clienteRetornoEm) return { estado: 'retornou', tipo: s.clienteRetornoTipo || 'outro' };
    if (!s.respostaEnviadaEm || !estaEmAndamento(s.status)) return { estado: 'sem_resposta_vigor' };
    const inicio = Date.parse(diaSP(s.respostaEnviadaEm) + 'T00:00:00Z');
    const hoje = Date.parse(hojeIso.slice(0, 10) + 'T00:00:00Z');
    const dias = Math.max(0, Math.round((hoje - inicio) / 86400000));
    return { estado: 'aguardando', dias, atrasado: dias >= DIAS_ALERTA_SEM_RETORNO_CLIENTE };
}

export const RETORNO_CLIENTE_LABEL: Record<string, string> = {
    confirmou: 'Confirmou o cancelamento',
    desistiu: 'Desistiu do cancelamento',
    negociar: 'Quer negociar',
    outro: 'Outro retorno',
};
